import { Fragment, useCallback } from 'react'
import {
    Controller,
    useFormContext,
    useFormState,
    useWatch,
} from 'react-hook-form'
import { Scissors, Trash2, Undo2 } from 'lucide-react'

import type { WorkflowSearchTracker } from '@/lib/order-workflow-contract'
import type { Product } from '@/lib/api/product-api'
import { formatVND } from '@/lib/format'
import {
    canSplitMaterial,
    productUnitPriceFor,
    showSplitToggle,
} from '@/lib/material-split'
import { m } from '@/lib/paraglide/messages'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ProductPicker } from '@/components/products/product-picker'

interface Props {
    workflow?: WorkflowSearchTracker
    index: number
    /** Optional warehouse id — used by ProductPicker to show remaining stock
     *  below the combobox for the selected product. */
    warehouseId?: string
    onRemove: (index: number) => void
}

/**
 * OrderItemRowRhf — biến thể dành cho form dùng RHF FormProvider (vd
 * /orders/new). Mỗi input đăng ký vào form state qua `Controller` →
 * keystroke chỉ trigger re-render của row hiện tại, không phải toàn form.
 *
 * Khác `OrderItemRow` (controlled với item + onChange): row này tự lấy
 * value qua `useWatch` (scope theo namePrefix) nên tránh được vòng
 * "parent watch toàn list → re-render → field.id đổi → input mất focus".
 */
export function OrderItemRowRhf({
    index,
    warehouseId,
    onRemove,
    workflow,
}: Props) {
    const { control, setValue, getFieldState } = useFormContext()

    const namePrefix = `items.${index}` as const
    const quantity = useWatch({ control, name: `${namePrefix}.quantity` }) ?? 0
    const unitPrice =
        useWatch({ control, name: `${namePrefix}.unit_price` }) ?? 0
    const discount =
        useWatch({ control, name: `${namePrefix}.discount_percent` }) ?? 0
    const productId = useWatch({ control, name: `${namePrefix}.product_id` })
    // product_id được set qua setValue (không qua Controller) nên lỗi zod "chưa
    // chọn sản phẩm" không tự hiện. Subscribe để render lỗi ngay tại ô chọn.
    const formState = useFormState({ control, name: namePrefix })
    const productError = getFieldState(
        `${namePrefix}.product_id`,
        formState,
    ).error
    const unit = useWatch({ control, name: `${namePrefix}.unit` }) ?? ''
    const split =
        useWatch({ control, name: `${namePrefix}.material_price_split` }) ??
        false
    const priceIncludesMaterial =
        useWatch({ control, name: `${namePrefix}.price_includes_material` }) ??
        false
    const materialPricePerUnit =
        useWatch({ control, name: `${namePrefix}.material_price_per_unit` }) ??
        0
    const masterUnitPrice =
        useWatch({ control, name: `${namePrefix}.master_unit_price` }) ?? 0

    const lineTotal =
        Number(quantity) * Number(unitPrice) * (1 - Number(discount) / 100)

    const canToggle = showSplitToggle(
        priceIncludesMaterial,
        materialPricePerUnit,
    )
    const splitAllowed = canSplitMaterial(masterUnitPrice, materialPricePerUnit)

    const handleProductChange = useCallback(
        (newProductId: string, product?: Product) => {
            setValue(`${namePrefix}.product_id`, newProductId, {
                shouldDirty: true,
            })
            if (!product) {
                setValue(`${namePrefix}.unit`, '', { shouldDirty: true })
                return
            }
            setValue(`${namePrefix}.product_name`, product.name, {
                shouldDirty: true,
            })
            setValue(`${namePrefix}.unit`, product.unit, { shouldDirty: true })
            // Chọn SP → mặc định GỘP: đơn giá = master (đã gồm giá vật tư). Chốt
            // master + M + cờ để nút tách/gộp và reset-from-master hoạt động.
            setValue(`${namePrefix}.unit_price`, product.unit_price, {
                shouldDirty: true,
                shouldValidate: true,
            })
            setValue(`${namePrefix}.master_unit_price`, product.unit_price, {
                shouldDirty: true,
            })
            setValue(
                `${namePrefix}.price_includes_material`,
                product.price_includes_material ?? false,
                { shouldDirty: true },
            )
            setValue(
                `${namePrefix}.material_price_per_unit`,
                product.material_price_per_unit ?? 0,
                { shouldDirty: true },
            )
            setValue(`${namePrefix}.material_price_split`, false, {
                shouldDirty: true,
            })
        },
        [namePrefix, setValue],
    )

    // Toggle tách/gộp — RESET đơn giá SP từ master (bỏ qua giá user sửa tay).
    const handleToggleSplit = useCallback(() => {
        const next = !split
        setValue(
            `${namePrefix}.unit_price`,
            productUnitPriceFor(masterUnitPrice, materialPricePerUnit, next),
            { shouldDirty: true, shouldValidate: true },
        )
        setValue(`${namePrefix}.material_price_split`, next, {
            shouldDirty: true,
        })
    }, [namePrefix, setValue, split, masterUnitPrice, materialPricePerUnit])

    const numericFields = [
        {
            key: 'quantity',
            label: m.order_row_quantity_label({ row: index + 1 }),
        },
        {
            key: 'unit_price',
            label: m.order_row_price_label({ row: index + 1 }),
        },
        {
            key: 'discount_percent',
            label: m.order_row_discount_label({ row: index + 1 }),
        },
    ] as const
    const errors = numericFields.flatMap(({ key }) => {
        const error = getFieldState(`${namePrefix}.${key}`, formState).error
        return error?.message ? [{ key, message: error.message }] : []
    })
    const hintId = `${namePrefix}.price-reset-hint`

    return (
        <Fragment>
            <tr className="border-b align-top">
                <td className="p-2">
                    <ProductPicker
                        workflow={workflow}
                        value={productId || ''}
                        onChange={handleProductChange}
                        warehouseId={warehouseId}
                        className="w-full min-w-0"
                    />
                    {productError && (
                        <p className="mt-1 text-xs text-destructive">
                            {productError.message}
                        </p>
                    )}
                </td>
                <td className="p-2 pt-4 text-sm text-muted-foreground whitespace-nowrap">
                    {unit || '—'}
                </td>
                {numericFields.map(({ key, label }) => (
                    <td className="p-2" key={key}>
                        <Controller
                            control={control}
                            name={`${namePrefix}.${key}`}
                            render={({ field, fieldState }) => (
                                <Input
                                    type="number"
                                    step="any"
                                    inputMode="decimal"
                                    aria-label={label}
                                    aria-invalid={Boolean(fieldState.error)}
                                    aria-describedby={
                                        fieldState.error
                                            ? `${namePrefix}.${key}-error`
                                            : undefined
                                    }
                                    className="w-full aria-invalid:border-destructive aria-invalid:focus-visible:border-destructive"
                                    value={field.value ?? ''}
                                    onChange={(e) =>
                                        field.onChange(Number(e.target.value))
                                    }
                                    onBlur={field.onBlur}
                                    ref={field.ref}
                                />
                            )}
                        />
                        {key === 'unit_price' && canToggle && (
                            <div className="mt-1">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={split ? 'secondary' : 'outline'}
                                    className="h-7 gap-1 px-2 text-xs"
                                    disabled={!split && !splitAllowed}
                                    aria-describedby={hintId}
                                    onClick={handleToggleSplit}
                                >
                                    {split ? (
                                        <Undo2 className="size-3" />
                                    ) : (
                                        <Scissors className="size-3" />
                                    )}
                                    {split
                                        ? m.order_material_merge_button()
                                        : m.order_material_split_button()}
                                </Button>
                                {!split && !splitAllowed && (
                                    <p className="mt-0.5 text-[11px] text-amber-600">
                                        {m.order_material_split_negative_hint()}
                                    </p>
                                )}
                            </div>
                        )}
                    </td>
                ))}
                <td className="p-2 pt-4 text-right font-medium whitespace-nowrap">
                    {formatVND(lineTotal)}
                </td>
                <td className="p-2">
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={m.order_row_remove_label({
                            row: index + 1,
                        })}
                        onClick={() => onRemove(index)}
                        className="text-destructive"
                    >
                        <Trash2 className="size-4" />
                    </Button>
                </td>
            </tr>
            {(errors.length > 0 || canToggle) && (
                <tr className="border-b">
                    <td colSpan={7} className="space-y-1 px-3 py-2">
                        {errors.map(({ key, message }) => (
                            <p
                                key={key}
                                id={`${namePrefix}.${key}-error`}
                                role="alert"
                                className="text-sm text-destructive"
                            >
                                {message}
                            </p>
                        ))}
                        {canToggle && (
                            <p
                                id={hintId}
                                className="text-xs text-muted-foreground"
                            >
                                {m.order_material_price_reset_hint()}
                            </p>
                        )}
                    </td>
                </tr>
            )}
        </Fragment>
    )
}
